/**
 * Enrollment caseload (GET /users/enrollment/members) in Redux with websocket-driven updates.
 *
 * Backend note: when member statuses change, {@link MemberStatusService} runs
 * {@link UserService.updateLatestMemberStatus}, which calls {@link UserService.broadcastUser}
 * — so {@link UserEventTypes.USER_UPDATED} carries fresh user rows including
 * `expectedAssignedGuideId` / `monthlyOutreach` / `memberStatus` / `newlyEngagedMemberCount`. Expected-guide batch updates
 * also call `broadcastUser`. We still listen for {@link UserMemberStatusEventTypes.USER_MEMBER_STATUS_CREATED}
 * with key `newly-engaged` and refetch the member as a safety net.
 */
import type {
  EnrollmentMembersListResponseDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import {
  UserMemberStatusEventTypes,
  UserMemberStatusWSPayload,
  UserUpdatedEventPayload,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { resetRedux } from '../actions/sharedActions';
import { membersRequests } from '../requests/members.request';
import type { RootState } from '../reemaStore.util';
import type { WebsocketEventActions } from '../types/websocketTypes';
import { createApiThunk } from '../utils/apiThunk';
import { addToEffectsMiddleware } from '../utils/effectsMiddleware.util';
import { getMember, memberSlice } from './memberSlice.slice';

export const getEnrollmentMembers = createApiThunk(
  membersRequests.getEnrollmentMembers
);

const enrollmentMemberAdapter = createEntityAdapter<UserDto>();

export type EnrollmentCaseloadState = {
  members: ReturnType<typeof enrollmentMemberAdapter.getInitialState>;
  orderedMemberIds: string[];
  listStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  totalCount: number;
};

const initialState: EnrollmentCaseloadState = {
  members: enrollmentMemberAdapter.getInitialState(),
  orderedMemberIds: [],
  listStatus: 'idle',
  totalCount: 0,
};

const enrollmentMemberSelectors = enrollmentMemberAdapter.getSelectors(
  (state: RootState) => state.enrollmentCaseload.members
);

function upsertEnrollmentFromUserUpdated(
  state: EnrollmentCaseloadState,
  payload: UserUpdatedEventPayload
): void {
  const id = payload.user.id;
  if (!state.orderedMemberIds.includes(id)) {
    return;
  }
  const original = state.members.entities[id];
  const patch = payload.user;
  enrollmentMemberAdapter.upsertOne(state.members, {
    ...(original ?? {}),
    ...patch,
    memberPermissions: original?.memberPermissions ?? patch.memberPermissions ?? [],
    userPermissions: original?.userPermissions ?? patch.userPermissions ?? [],
  });
}

export const enrollmentCaseloadSlice = createSlice({
  name: 'enrollmentCaseload',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, () => initialState)
      .addCase(getEnrollmentMembers.pending, state => {
        state.listStatus = 'loading';
      })
      .addCase(
        getEnrollmentMembers.fulfilled,
        (
          state,
          action: PayloadAction<EnrollmentMembersListResponseDto>
        ) => {
          state.listStatus = 'succeeded';
          const users = action.payload.users ?? [];
          state.orderedMemberIds = users.map(u => u.id);
          state.totalCount = action.payload.count ?? users.length;
          enrollmentMemberAdapter.setAll(state.members, users);
        }
      )
      .addCase(getEnrollmentMembers.rejected, state => {
        state.listStatus = 'failed';
      })
      .addCase(memberSlice.actions.userUpdated, (state, action) => {
        upsertEnrollmentFromUserUpdated(state, action.payload);
      })
      .addCase(getMember.fulfilled, (state, action: PayloadAction<UserDto>) => {
        const id = action.payload.id;
        if (!state.orderedMemberIds.includes(id)) {
          return;
        }
        const original = state.members.entities[id];
        enrollmentMemberAdapter.upsertOne(state.members, {
          ...action.payload,
          memberPermissions:
            original?.memberPermissions ??
            action.payload.memberPermissions ??
            [],
          userPermissions:
            original?.userPermissions ?? action.payload.userPermissions ?? [],
        });
      });
  },
});

/** Dispatched from websocket; handled in middleware (refetch when newly-engaged). */
export const enrollmentUserMemberStatusCreatedWs =
  createAction<UserMemberStatusWSPayload>(
    'enrollmentCaseload/wsUserMemberStatusCreated'
  );

export const selectEnrollmentOrderedMembers = createSelector(
  [
    (state: RootState) => state.enrollmentCaseload.orderedMemberIds,
    (state: RootState) =>
      enrollmentMemberSelectors.selectEntities(state),
  ],
  (orderedIds, entities): UserDto[] =>
    orderedIds
      .map(id => entities[id])
      .filter((u): u is UserDto => u !== undefined)
);

export const selectEnrollmentMemberEntityById = (
  state: RootState,
  memberId: string | null | undefined
): UserDto | undefined =>
  memberId
    ? enrollmentMemberSelectors.selectById(state, memberId)
    : undefined;

export const selectEnrollmentCaseloadTotalCount = (state: RootState) =>
  state.enrollmentCaseload.totalCount;

export const selectEnrollmentPreviewEngagementOverlay = createSelector(
  [
    (state: RootState, memberId: string | undefined) =>
      memberId
        ? enrollmentMemberSelectors.selectById(state, memberId)
        : undefined,
  ],
  (
    entity
  ):
    | Pick<
        UserDto,
        'expectedAssignedGuideId' | 'monthlyOutreach' | 'memberStatus'
      >
    | undefined =>
    entity
      ? {
          expectedAssignedGuideId: entity.expectedAssignedGuideId,
          monthlyOutreach: entity.monthlyOutreach,
          memberStatus: entity.memberStatus,
        }
      : undefined
);

export const enrollmentCaseloadWebsocketActions: WebsocketEventActions[] = [
  {
    type: UserMemberStatusEventTypes.USER_MEMBER_STATUS_CREATED,
    action: enrollmentUserMemberStatusCreatedWs,
    channel: 'baseSocket',
  },
];

addToEffectsMiddleware(addEffect => {
  addEffect({
    action: enrollmentUserMemberStatusCreatedWs,
    effect: async ({ action, dispatch, state }) => {
      if (action.payload.userMemberStatus.key !== 'newly-engaged') {
        return;
      }
      const memberId = action.payload.userMemberStatus.memberId;
      if (!state.enrollmentCaseload.orderedMemberIds.includes(memberId)) {
        return;
      }
      void dispatch(getMember({ params: { id: memberId } }));
    },
  });
});

export const enrollmentCaseloadReducer = enrollmentCaseloadSlice.reducer;
